import { Check } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { toast } from "sonner";
import { Toaster } from "~/components/Toaster";
import { Avatar } from "~/components/Avatar";
import { EventFormInputType, type EventFragment } from "~/__generated__/graphql/graphql";
import { TextField } from "~/components/Fields";
import { Text } from "~/components/Text";
import { CheckoutQuestionStep } from "./CheckoutQuestionStep";
import { useCheckoutSession } from "./CheckoutSession";
import type { CheckoutDraft } from "./checkout-state";

export function ApprovalQuestionsStep({ plan, onComplete }: { plan: EventFragment; onComplete: () => void }) {
  const messageId = useId();
  const toasterId = useId();
  const toastId = useRef<string | number | undefined>(undefined);
  const questionsRef = useRef<HTMLDivElement>(null);
  const { draft, update, flush, canContinue, setStep } = useCheckoutSession();
  const [validationAttempt, setValidationAttempt] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => () => {
    if (toastId.current !== undefined) toast.dismiss(toastId.current);
  }, []);
  useEffect(() => {
    if (!validationAttempt) return;
    const field = questionsRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]');
    const target = field?.matches("input, button")
      ? field
      : field?.querySelector<HTMLElement>("input, button");
    target?.focus({ preventScroll: true });
    target?.scrollIntoView({ block: "center" });
  }, [validationAttempt]);
  useEffect(() => {
    if (Object.keys(draft.answers).length || !plan.myRequest || plan.myRequest.submittedAt) return;
    const answers: CheckoutDraft["answers"] = {};
    for (const input of plan.formInputs) {
      const answer = (plan.myRequest.form ?? []).find(answer => (answer.formQuestionID ? answer.formQuestionID === input.id : answer.formQuestionLabel === input.label) && answer.formQuestionType === input.type);
      if (answer) answers[input.id] = { text: input.type === EventFormInputType.Text ? answer.value : "", selected: input.type === EventFormInputType.Select ? (answer.selectedValues ?? answer.value.split(", ")).filter(value => input.selectOptions.includes(value)) : [] };
    }
    if (Object.keys(answers).length) update({ answers });
  }, [draft.answers, plan.formInputs, plan.myRequest, update]);

  const fieldErrors: Record<string, string> = {};
  for (const input of plan.formInputs) {
    const answer = draft.answers[input.id];
    if (input.type === EventFormInputType.Text) {
      if (!answer?.text.trim()) fieldErrors[input.id] = "Enter your answer.";
    } else if (!answer?.selected.some(value => input.selectOptions.includes(value))) {
      fieldErrors[input.id] = "Select at least one option.";
    }
  }

  const onToastClosed = ({ id }: { id: string | number }) => {
    if (toastId.current === id) toastId.current = undefined;
  };

  const submit = async () => {
    if (Object.keys(fieldErrors).length) {
      setValidationAttempt(attempt => attempt + 1);
      toastId.current = toast.error("Answer the host’s questions to continue.", {
        id: toastId.current,
        toasterId,
        onDismiss: onToastClosed,
        onAutoClose: onToastClosed,
      });
      return;
    }
    if (toastId.current !== undefined) toast.dismiss(toastId.current);
    toastId.current = undefined;
    setLoading(true);
    if (await flush() && canContinue()) onComplete();
    setLoading(false);
  };
  const host = plan.hosts.find(host => host.primary);
  const hostFirstName = host?.user.firstName?.trim();
  const hasQuestions = plan.formInputs.length > 0;
  return (
    <CheckoutQuestionStep title={hasQuestions ? `Questions from ${hostFirstName || "the host"}` : `Say hi to ${hostFirstName || "the host"}`}
      description={hasQuestions ? undefined : `${hostFirstName || "The host"} reads every note before approving guests. Give them something to go on.`}
      onBack={() => setStep("profile")}
      onSubmit={submit} loading={loading} submitLabel="Next">
      <div ref={questionsRef} className="flex flex-col gap-5">
        {hasQuestions && (
          <div className="flex items-center gap-2.5">
            {host && <Avatar user={host.user} size={38} className="shrink-0" />}
            <Text as="p" variant="secondary" size="sm" className="leading-relaxed">
              <span className="font-bold">{hostFirstName || "The host"}</span>{" "}
              is putting the guest list together. Give them something to go on.
            </Text>
          </div>
        )}
        {plan.formInputs.map(input => {
          const error = validationAttempt ? fieldErrors[input.id] : undefined;
          const errorId = `${messageId}-${input.id}-error`;
          return input.type === EventFormInputType.Select ? (
            <fieldset key={input.id} className="flex flex-col gap-2.5"
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? errorId : undefined}>
              <legend className="font-sans text-sm font-bold text-butter-green">{input.label}</legend>
              {error && <Text as="p" id={errorId} role="alert" variant="secondary" size="sm" color="pink">{error}</Text>}
              <div className="flex flex-col gap-2">
                {input.selectOptions.map(option => {
                  const values = draft.answers[input.id]?.selected ?? [];
                  const selected = values.includes(option);
                  return <button key={option} type="button" role="checkbox" aria-checked={selected}
                    onClick={() => update({ answers: { ...draft.answers, [input.id]: { text: "", selected: selected ? values.filter(value => value !== option) : [...values, option] } } })}
                    className={`flex min-h-12 items-center justify-between gap-3 rounded-xl border px-4 py-3 text-left font-sans text-base transition-colors ${selected ? "border-butter-green bg-butter-green text-butter-cream-light" : `${error ? "border-butter-pink" : "border-butter-cream"} bg-white text-butter-green hover:bg-butter-cream/30`}`}>
                    <span>{option}</span><span aria-hidden className={`flex size-6 shrink-0 items-center justify-center rounded-md border ${selected ? "border-butter-cream-light bg-butter-cream-light text-butter-green" : "border-butter-cream"}`}>
                      {selected && <Check className="size-4" strokeWidth={3} />}
                    </span>
                  </button>;
                })}
              </div>
              <Text as="p" variant="secondary" size="sm">Select all that apply.</Text>
            </fieldset>
          ) : <TextField key={input.id} label={input.label} placeholder={input.placeholder ?? ""} value={draft.answers[input.id]?.text ?? ""}
            error={error}
            onChange={event => update({ answers: { ...draft.answers, [input.id]: { text: event.target.value, selected: [] } } })} />;
        })}
        <div className="flex flex-col gap-1.5 font-sans text-sm font-bold text-butter-green">
          <label htmlFor={messageId} className="flex items-baseline justify-between gap-3"><span className="min-w-0 wrap-break-word">Message to {hostFirstName || "the host"}</span><span className="shrink-0 font-normal">(optional)</span></label>
          <textarea id={messageId} value={draft.message} onChange={event => update({ message: event.target.value })} placeholder="Say hi or share what caught your eye…" maxLength={500} rows={4}
            className="min-h-28 resize-y rounded-[9px] border border-butter-cream bg-white px-3 py-3 font-sans text-base font-normal text-butter-green outline-none transition-colors placeholder:text-placeholder-text focus:border-butter-green/60" />
        </div>
      </div>
      <Toaster id={toasterId} />
    </CheckoutQuestionStep>
  );
}
